/**
 * Componentes básicos de interface (Tailwind + tokens do Design System café — Fase B),
 * compartilhados entre o ERP Web e o CaféGestor Vendedor (A0). As assinaturas são as mesmas de
 * antes. Componentes de PÁGINA do Web (cabeçalho com navegação, estados, etapas…) continuam em
 * `apps/web/src/components` — inclusive `PageTitle`, que usa o cabeçalho do Web.
 */
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentPropsWithRef,
  ReactNode,
} from "react";
import { buttonClasses, type ButtonSize, type ButtonVariant } from "./button-styles";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

export function Button({
  variant = "primary",
  size = "md",
  className,
  type = "button",
  ...rest
}: ButtonProps) {
  return <button type={type} className={buttonClasses(variant, size, className)} {...rest} />;
}

/**
 * Link com aparência de botão. Para baixar ou abrir arquivo, o clique da pessoa vai direto ao
 * link (o navegador trata como ação do usuário: não cai no bloqueio de downloads automáticos).
 */
export function LinkButton({
  variant = "secondary",
  size = "md",
  className,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <a className={buttonClasses(variant, size, className)} {...rest} />;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | undefined;
  /** campo obrigatório: asterisco visível (o nome acessível do campo não muda) */
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-foreground">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-destructive" title="obrigatório">
            *
          </span>
        )}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint && !error && (
        <span className="mt-1.5 block text-[13px] leading-snug text-muted-foreground">{hint}</span>
      )}
      {error && (
        <span className="mt-1.5 block text-[13px] font-medium leading-snug text-destructive">
          {error}
        </span>
      )}
    </label>
  );
}

const control =
  "w-full rounded-lg border border-input bg-card text-[15px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground/80 focus:border-caramel focus:ring-4 focus:ring-caramel/20 disabled:cursor-not-allowed disabled:bg-muted aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/15";

export function Input({ className, ...rest }: ComponentPropsWithRef<"input">) {
  return <input className={cx("h-11 px-3.5", control, className)} {...rest} />;
}

export function Select({ className, ...rest }: ComponentPropsWithRef<"select">) {
  return <select className={cx("h-11 px-3", control, className)} {...rest} />;
}

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx("rounded-xl border bg-card text-card-foreground shadow-card", className)}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 px-5 py-4">
          {title && <h2 className="text-[17px] font-semibold">{title}</h2>}
          {actions}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export type Tone = "neutral" | "ok" | "warn" | "error" | "info";

export function Alert({
  tone = "info",
  children,
}: {
  tone?: "info" | "warn" | "error" | "ok";
  children: ReactNode;
}) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cx(
        "rounded-lg border px-4 py-3 text-sm leading-relaxed",
        tone === "info" && "border-border bg-muted text-secondary-foreground",
        tone === "warn" && "border-warning/30 bg-warning-soft text-warning-strong",
        tone === "error" && "border-destructive/30 bg-destructive-soft text-destructive",
        tone === "ok" && "border-success/30 bg-success-soft text-success-strong",
      )}
    >
      {children}
    </div>
  );
}

const BADGE_TONE: Record<Tone, string> = {
  neutral: "bg-muted text-secondary-foreground",
  ok: "bg-success-soft text-success",
  warn: "bg-warning-soft text-warning-strong",
  error: "bg-destructive-soft text-destructive",
  info: "bg-info-soft text-info",
};

/**
 * Etiqueta. Com tom (ok/aviso/erro/info) é uma situação: cor + ponto + texto (nunca só a cor).
 * `dot={false}` tira o ponto; a neutra não tem ponto, a não ser com `dot`.
 */
export function Badge({
  children,
  tone = "neutral",
  dot,
}: {
  children: ReactNode;
  tone?: Tone;
  dot?: boolean;
}) {
  const showDot = dot ?? tone !== "neutral";
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        BADGE_TONE[tone],
      )}
    >
      {showDot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Spinner({ label = "Carregando..." }: { label?: string }) {
  return (
    <p
      role="status"
      className="flex items-center justify-center gap-3 py-10 text-sm text-muted-foreground"
    >
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-caramel/30 border-t-caramel"
      />
      {label}
    </p>
  );
}

/** Colunas de número/valor: o cabeçalho fica à direita, como os valores. */
const NUMERIC_HEAD =
  /^(total|valor|vl\.?|qtd\.?|quant|saldo|pre[çc]o|subtotal|custo|desconto|recebido|pago|em aberto|frete|base|al[íi]q|icms|ipi|pis|cofins|perda|rendimento|dispon[íi]vel|reservado|contado|diferen[çc]a|esperado|planejad|produzid|consumido\b|m[íi]nimo|m[áa]ximo)/i;

/**
 * Tabela padrão: cabeçalho destacado, linhas confortáveis e realce ao passar o mouse.
 * As células das telas continuam como estão; o espaçamento vem daqui.
 */
export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table
        className={cx(
          "w-full text-left text-sm",
          "[&_tbody_td]:py-3 [&_tbody_td]:align-middle [&_tbody_tr]:transition-colors [&_tbody_tr:hover]:bg-background",
        )}
      >
        <thead className="bg-table-head text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <tr>
            {head.map((h, i) => (
              <th
                key={`${h}-${i}`}
                scope="col"
                className={cx(
                  "px-3 py-3 font-semibold",
                  NUMERIC_HEAD.test(h.trim()) && "text-right",
                )}
              >
                {h.trim() === "" ? <span className="sr-only">Ações</span> : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">{children}</tbody>
      </table>
    </div>
  );
}

/** Situação (NF-e, título, pedido…): sempre cor + ponto + texto, nunca só a cor. */
export function StatusBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <Badge tone={tone} dot>
      {children}
    </Badge>
  );
}
